import { Link } from "@tanstack/react-router";
import type { SVGProps } from "react";

import icon from "@/assets/images/icon.png";
import { m } from "@/paraglide/messages.js";

// Section links are absolute (`/#anchor`) so the footer also works on pages
// other than the landing page, such as /contact.
const FOOTER_COLUMNS = [
  {
    heading: m.landing_footer_column_platform(),
    links: [
      { label: m.landing_nav_capabilities(), href: "/#capabilities" },
      { label: m.landing_nav_how_it_works(), href: "/#how-it-works" },
      { label: m.landing_footer_link_security(), href: "/#capabilities" },
      { label: m.landing_footer_link_market_data(), href: "/#capabilities" },
      { label: m.landing_nav_faq(), href: "/#faq" },
    ],
  },
  {
    heading: m.landing_footer_column_company(),
    links: [
      { label: m.landing_nav_leadership(), href: "/#leadership" },
      { label: m.landing_footer_link_contact(), href: "/contact" },
      { label: m.landing_footer_link_privacy(), href: "/privacy" },
      { label: m.landing_footer_link_terms(), href: "/terms" },
    ],
  },
  {
    heading: m.landing_footer_column_access(),
    links: [
      { label: m.landing_cta_request_demo(), href: "/contact" },
      { label: m.landing_cta_client_sign_in(), href: "/login" },
    ],
  },
] as const;

const SOCIAL = [
  { label: "X", href: "https://x.com" },
  { label: "LinkedIn", href: "https://www.linkedin.com" },
] as const;

/**
 * Multi-column marketing footer: wordmark and positioning line on the left,
 * link columns and social icons on the right, company registration details
 * along the bottom.
 */
export const LandingFooter = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="">
      <div className="mx-auto flex w-full max-w-[1320px] flex-col items-start justify-between gap-10 px-6 py-12 md:flex-row md:py-16">
        <div className="flex flex-col gap-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={icon} alt="" className="size-7 rounded-lg" />
            <span className="text-lg font-semibold text-foreground">{m.app_title()}</span>
          </Link>
          <p className="text-sm font-medium text-muted-foreground">{m.app_description()}</p>
          <div className="flex items-center gap-4">
            {SOCIAL.map((entry) => (
              <a
                key={entry.label}
                href={entry.href}
                target="_blank"
                rel="noreferrer"
                aria-label={entry.label}
                className="text-muted-foreground transition-colors hover:text-primary"
              >
                <SocialIcon label={entry.label} className="size-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="grid w-full grid-cols-2 gap-8 md:w-auto md:grid-cols-3 md:gap-12">
          {FOOTER_COLUMNS.map((column) => (
            <div key={column.heading} className="flex flex-col gap-3">
              <h3 className="text-sm font-medium text-muted-foreground">{column.heading}</h3>
              <div className="flex flex-col gap-2">
                {column.links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-sm text-foreground transition-colors hover:text-primary"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="">
        <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-1 px-6 py-6 text-center text-xs text-muted-foreground">
          <p>{m.landing_footer_registration()}</p>
          <p>{m.landing_footer_copyright({ year, name: m.app_title() })}</p>
        </div>
      </div>
    </footer>
  );
};

function SocialIcon({ label, className }: { label: string; className?: string }) {
  switch (label) {
    case "X":
      return <XIcon className={className} />;
    case "LinkedIn":
      return <LinkedInIcon className={className} />;
    default:
      return null;
  }
}

function XIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.91l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function LinkedInIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}
